import React, { useState, useEffect, Suspense, lazy } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { 
  Users, Ticket, FileText, TrendingUp, Clock, CheckCircle, AlertCircle,
  Building2, BarChart3, Calendar, Bell, ClipboardList, ArrowRight
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import axios from 'axios';

// All heavy dashboard imports are lazy-loaded to prevent Babel plugin crash
const HRDashboard              = lazy(() => import('./HRDashboard'));
const BarterDashboard          = lazy(() => import('./BarterDashboard'));
const FinanceDashboard         = lazy(() => import('./FinanceDashboard'));
const MarketingDashboard       = lazy(() => import('./MarketingDashboard'));
const AdminDepartmentDashboard = lazy(() => import('./AdminDepartmentDashboard'));
const CustomerDashboard        = lazy(() => import('./CustomerDashboard'));
const DMDashboardComponent     = lazy(() => import('./DMDashboard'));
const ADMDashboard             = lazy(() => import('./ADMDashboard'));
const LegalDashboard           = lazy(() => import('./LegalDashboard'));
const OperationsDashboard      = lazy(() => import('./OperationsDashboard'));
const SiteOpsDashboard         = lazy(() => import('./SiteOpsDashboard'));
const ArchitectDashboard       = lazy(() => import('./ArchitectDashboard'));
const VehicleDashboard         = lazy(() => import('./VehicleDashboard'));
const ITDashboard              = lazy(() => import('./ITDashboard'));
const CMDDashboard             = lazy(() => import('./CMDDashboard'));
const GreenyuvaDashboard       = lazy(() => import('./GreenyuvaDashboard'));
const DigitalMarketingDashboard= lazy(() => import('./DigitalMarketingDashboard'));
const CPTrackerDashboard       = lazy(() => import('./CPTrackerDashboard'));
const HetalDashboard           = lazy(() => import('./HetalDashboard'));
const MohineeDashboard         = lazy(() => import('./MohineeDashboard'));
const MySheetsTab              = lazy(() => import('./MySheetsTab'));
const ProfileOverviewCard      = lazy(() => import('./ProfileOverviewCard'));
const CommissionStructureCard  = lazy(() => import('./CommissionStructureCard'));
const DashboardHeader         = lazy(() => import('./DashboardHeader'));
const CallActionWidget         = lazy(() => import('./CallActionWidget'));

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const colorClasses = {
  emerald: { bg: 'bg-emerald-100', text: 'text-emerald-600' },
  amber: { bg: 'bg-amber-100', text: 'text-amber-600' },
  red: { bg: 'bg-red-100', text: 'text-red-600' },
  stone: { bg: 'bg-stone-100', text: 'text-stone-600' },
  blue: { bg: 'bg-blue-100', text: 'text-blue-600' },
};

const StatCard = ({ icon: Icon, title, value, subtitle, color = 'emerald' }) => {
  const colors = colorClasses[color] || colorClasses.emerald;
  return (
    <Card className="hover:shadow-lg transition-shadow">
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-stone-500 font-medium">{title}</p>
            <p className="text-3xl font-bold text-stone-900 mt-1">{value}</p>
            {subtitle && <p className="text-xs text-stone-400 mt-1">{subtitle}</p>}
          </div>
          <div className={'p-3 rounded-lg ' + colors.bg}>
            {React.createElement(Icon, {className: 'w-6 h-6 ' + colors.text})}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const DashboardHome = () => {
  const { user } = useOutletContext();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalTickets: 0,
    openTickets: 0,
    documents: 0,
    teamMembers: 0
  });
  const [recentTickets, setRecentTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    const token = localStorage.getItem('crm_token');
    const headers = { Authorization: 'Bearer ' + token };
    try {
      // Fetch tickets
      const ticketsRes = await axios.get(API + '/crm/tickets', { headers });
      const tickets = ticketsRes.data;
      
      // Fetch documents
      const docsRes = await axios.get(API + '/crm/documents', { headers });

      let pendingApprovals = 0;
      let teamMembers = 0;

      // Fetch pending approvals for admin users
      if (user?.role === 'super_admin' || user?.role === 'department_head') {
        try {
          const usersRes = await axios.get(API + '/crm/admin/users', { headers });
          pendingApprovals = usersRes.data.filter(u => u.status === 'pending').length;
        } catch (e) {
        }
      }

      // Fetch team members for DM/ADM roles
      if (user?.role === 'dm' || user?.role === 'adm') {
        try {
          const teamRes = await axios.get(API + '/crm/hierarchy/team', { headers });
          teamMembers = teamRes.data.length;
        } catch (e) {
        }
      }
      
      setStats({
        totalTickets: tickets.length,
        openTickets: tickets.filter(t => t.status === 'open' || t.status === 'in_progress').length,
        documents: docsRes.data.length,
        teamMembers,
        pendingApprovals
      });
      
      setRecentTickets(tickets.slice(0, 5));
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 17) return 'Good Afternoon';
    return 'Good Evening';
  };

  const getRoleDashboard = () => {
    // Highest priority: CMD
    if (user?.role === 'cmd') {
      return <CMDDashboard user={user} />;
    }

    // super_admin (Aditya / IT Admin) → CMDDashboard with full access
    if (user?.role === 'super_admin') {
      return <CMDDashboard user={user} />;
    }

    // channel_admin (Greenyuva / partner company admins) → restricted Greenyuva DM view only
    if (user?.role === 'channel_admin') {
      return <GreenyuvaDashboard user={user} />;
    }
    
    // Then check by department for department-specific dashboards
    switch (user?.department) {
      case 'hr':
        return <HRDashboard user={user} />;
      case 'barter':
        return <BarterDashboard user={user} />;
      case 'finance':
        return <FinanceDashboard user={user} />;
      case 'vehicle_maintenance':
        return <VehicleDashboard user={user} />;
      case 'site_ops':
        return <SiteOpsDashboard user={user} />;
      case 'marketing':
        if (user?.section === 'digital_marketing' || user?.marketingSection === 'digital_marketing') {
          return <DigitalMarketingDashboard user={user} />;
        }
        if (user?.section === 'cp_tracker') {
          return <CPTrackerDashboard user={user} />;
        }
        return <MarketingDashboard user={user} />;
      case 'admin':
        if (user?.role === 'super_admin') {
          return <CMDDashboard user={user} />;
        }
        return <AdminDepartmentDashboard user={user} />;
      case 'customer':
        return <CustomerDashboard user={user} />;
      case 'legal':
        return <LegalDashboard user={user} />;
      case 'receptionist':
        if (user?.subRole === 'mohinee') return <MohineeDashboard user={user} />;
        return <HetalDashboard user={user} />;
      case 'operations':
        return <OperationsDashboard user={user} />;
      case 'architect':
        return <ArchitectDashboard user={user} />;
      case 'it':
        return <ITDashboard user={user} />;
      case 'dm_channel':
        // Check role within DM channel
        if (user?.role === 'dm') {
          return <DMDashboardComponent user={user} />;
        } else if (user?.role === 'adm') {
          return <ADMDashboard user={user} />;
        } else if (user?.role === 'cp') {
          return <CPDashboard user={user} stats={stats} recentTickets={recentTickets} />;
        }
        return <DMDashboardComponent user={user} />;
      default:
        break;
    }
    
    // Fallback to role-based if department doesn't match
    switch (user?.role) {
      case 'cmd':
        return <CMDDashboard user={user} />;
      case 'super_admin':
        return <CMDDashboard user={user} />;
      case 'department_head':
        return <AdminDashboard user={user} stats={stats} recentTickets={recentTickets} />;
      case 'dm':
        return <DMDashboardComponent user={user} />;
      case 'adm':
        return <ADMDashboard user={user} />;
      case 'cp':
        return <CPDashboard user={user} stats={stats} recentTickets={recentTickets} />;
      case 'customer':
        return <CustomerDashboard user={user} />;
      default:
        return <EmployeeDashboard user={user} stats={stats} recentTickets={recentTickets} />;
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-900"></div>
      </div>
    );
  }

  // Departments that render their own full-page header — skip the common wrapper header
  const SELF_HEADED = ['marketing', 'finance', 'receptionist', 'legal', 'barter', 'hr', 'vehicle_maintenance', 'site_ops'];
  const isHierarchyRole = ['dm', 'adm', 'cp'].includes((user?.role || '').toLowerCase());
  // Hierarchy roles (dm/adm/cp) manage their own header inside DMDashboard — skip the generic wrapper
  const skipHeader = SELF_HEADED.includes(user?.department) || isHierarchyRole || user?.role === 'cmd' || user?.role === 'super_admin' || user?.role === 'channel_admin';

  // Shared checklist banner shown to ALL employees (skip for CMD only; super_admin sees it to access their own Index 1&2)
  const isCMD = user?.role === 'cmd';
  const checklistBanner = !isCMD && (
    <div
      className="mb-4 rounded-2xl px-5 py-4 flex flex-wrap items-center gap-4 shadow-lg"
      style={{ background: 'linear-gradient(135deg,#0f172a 0%,#1e3a8a 50%,#1f3864 100%)' }}
      data-testid="checklist-banner"
    >
      <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center flex-shrink-0">
        <ClipboardList className="w-5 h-5 text-amber-300" />
      </div>
      <div className="flex-1 min-w-[180px]">
        <p className="text-white font-bold text-sm">Daily Checklist</p>
        <p className="text-blue-200 text-xs mt-0.5">
          {isHierarchyRole
            ? 'Fill your Index 1 & 2 attendance, then update your Information Form'
            : 'Fill your daily Index 1 & 2 attendance. All entries auto-route to HR.'}
        </p>
      </div>
      <button
        onClick={() => navigate('/dashboard/checklist')}
        data-testid="proceed-to-checklist-btn"
        className="flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold text-sm px-4 py-2.5 rounded-xl shadow-md transition-all group"
      >
        <span>Proceed to Index 1 &amp; 2</span>
        <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
      </button>
    </div>
  );

  if (skipHeader) {
    return (
      <div className="p-6 lg:p-8">
        <Suspense fallback={<div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-500"></div></div>}>
          {getRoleDashboard()}
        </Suspense>
      </div>
    );
  }
  const dateStr = now.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  const timeStr = now.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true });

  return (
    <div className="p-6 lg:p-8">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-stone-900">
              {getGreeting()}, {user?.firstName}!
            </h1>
            <p className="text-stone-500 mt-1">
              Welcome to your dashboard. Here&apos;s what&apos;s happening today.
            </p>
          </div>
          <div className="text-right flex-shrink-0">
            <p className="text-xl font-bold text-stone-800 tabular-nums" data-testid="dashboard-live-time">{timeStr}</p>
            <p className="text-sm text-stone-500 mt-0.5" data-testid="dashboard-live-date">{dateStr}</p>
          </div>
        </div>

        {user?.department === 'dm_channel' && (
          <Suspense fallback={null}>
            <ProfileOverviewCard user={user} />
          </Suspense>
        )}

        {/* Checklist banner — visible to ALL employees (DM/ADM/CP + 42 staff) */}
        {checklistBanner}
      </div>

      <Suspense fallback={<div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-500"></div></div>}>
        {getRoleDashboard()}
      </Suspense>
    </div>
  );
};

// Admin/Department Head Dashboard
const AdminDashboard = ({ user, stats, recentTickets }) => (
  <>
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
      <StatCard icon={Ticket} title="Total Tickets" value={stats.totalTickets} subtitle="All time" />
      <StatCard icon={AlertCircle} title="Open Tickets" value={stats.openTickets} subtitle="Needs attention" color="amber" />
      <StatCard icon={FileText} title="Documents" value={stats.documents} subtitle="Uploaded" />
      <StatCard icon={Users} title="Pending Approvals" value={stats.pendingApprovals || 0} subtitle="User registrations" color={stats.pendingApprovals > 0 ? "red" : "stone"} />
    </div>

    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Ticket className="w-5 h-5 text-emerald-600" />
            Recent Tickets
          </CardTitle>
        </CardHeader>
        <CardContent>
          {recentTickets.length === 0 ? (
            <p className="text-stone-500 text-center py-4">No tickets yet</p>
          ) : (
            <div className="space-y-3">
              {recentTickets.map((ticket) => (
                <div key={ticket.ticketId} className="flex items-center justify-between p-3 bg-stone-50 rounded-lg">
                  <div>
                    <p className="font-medium text-stone-900">{ticket.subject}</p>
                    <p className="text-xs text-stone-500">{ticket.ticketId} • {ticket.raisedByName}</p>
                  </div>
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    ticket.status === 'open' ? 'bg-amber-100 text-amber-700' :
                    ticket.status === 'resolved' ? 'bg-green-100 text-green-700' :
                    'bg-stone-100 text-stone-700'
                  }`}>
                    {ticket.status}
                  </span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-amber-600" />
            Quick Actions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <a href="/dashboard/users" className="block p-3 bg-emerald-50 rounded-lg hover:bg-emerald-100 transition-colors">
              <p className="font-medium text-emerald-900">Manage Users</p>
              <p className="text-xs text-emerald-700">Approve pending registrations</p>
            </a>
            <a href="/dashboard/tickets" className="block p-3 bg-amber-50 rounded-lg hover:bg-amber-100 transition-colors">
              <p className="font-medium text-amber-900">View All Tickets</p>
              <p className="text-xs text-amber-700">Manage department tickets</p>
            </a>
          </div>
        </CardContent>
      </Card>
    </div>
  </>
);

// DM/ADM Dashboard (Basic)
const DMDashboardBasic = ({ user, stats, recentTickets }) => (
  <>
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
      <StatCard icon={Users} title="Team Members" value={stats.teamMembers || 0} subtitle="Under you" />
      <StatCard icon={Ticket} title="Open Tickets" value={stats.openTickets} subtitle="Needs attention" color="amber" />
      <StatCard icon={FileText} title="Documents" value={stats.documents} subtitle="Uploaded" />
      <StatCard icon={TrendingUp} title="Performance" value="Good" subtitle="This month" color="emerald" />
    </div>

    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card>
        <CardHeader>
          <CardTitle>Team Overview</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-stone-500 text-center py-8">
            View and manage your team from the "My Team" section
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Recent Activity</CardTitle>
        </CardHeader>
        <CardContent>
          {recentTickets.length === 0 ? (
            <p className="text-stone-500 text-center py-4">No recent activity</p>
          ) : (
            <div className="space-y-3">
              {recentTickets.slice(0, 3).map((ticket) => (
                <div key={ticket.ticketId} className="flex items-center gap-3 p-3 bg-stone-50 rounded-lg">
                  <Ticket className="w-5 h-5 text-amber-600" />
                  <div>
                    <p className="font-medium text-stone-900">{ticket.subject}</p>
                    <p className="text-xs text-stone-500">{ticket.ticketId}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  </>
);

// CP Dashboard — real counts derived from the CP's own Top 10 workbook (ADM team + customer leads)
const CPDashboard = ({ user, stats, recentTickets }) => {
  const [cpStats, setCpStats] = useState({ teamCount: 0, customerCount: 0, bookingCount: 0 });
  const [cpLeads, setCpLeads] = useState([]);
  const [cpLeadMeta, setCpLeadMeta] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem('crm_token');
        const [top10Res, leadRes, metaRes] = await Promise.all([
          axios.get(`${API}/my-sheets/top10`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/my-sheets/top100lead`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: null })),
          axios.get(`${API}/my-sheets/lead-meta/all`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        ]);
        const rows = (top10Res.data?.cells || []).slice(top10Res.data?.headerRows || 4);
        const teamCount = rows.filter((r) => r && String(r[1] || '').trim()).length;
        const customerCount = rows.filter((r) => r && String(r[13] || '').trim()).length;
        const bookingCount = rows.filter((r) => r && String(r[20] || '').trim()).length;
        setCpStats({ teamCount, customerCount, bookingCount });

        // Parse top100lead for the call widget
        if (leadRes.data?.cells) {
          const flagTrue = (v) => !['', '0', '×', 'x', 'no', 'n', '-'].includes(String(v || '').trim().toLowerCase());
          const leadRows = leadRes.data.cells.slice(leadRes.data.headerRows || 2);
          const parsed = leadRows
            .filter((r) => r && String(r[2] || '').trim())
            .map((r) => ({
              name: r[2] || '—', phone: String(r[3] || '').trim(),
              callDone: flagTrue(r[6]), officeVisit: flagTrue(r[7]),
              projectVisit: flagTrue(r[8]), booking: flagTrue(r[9]),
            }));
          setCpLeads(parsed);
        }

        // Build leadMeta map for remarks
        const metaList = Array.isArray(metaRes.data) ? metaRes.data : [];
        const metaMap = {};
        metaList.forEach(m => { if (m.phone) metaMap[m.phone] = m; });
        setCpLeadMeta(metaMap);
      } catch (e) {}
    })();
  }, []);

  const overviewContent = (
    <div className="space-y-6" data-testid="cp-overview-content">
      <Suspense fallback={null}>
        <DashboardHeader title="My Marketing Workbook" />
      </Suspense>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard icon={Users} title="My Customers" value={cpStats.customerCount} subtitle="From Top 10 workbook" />
        <StatCard icon={Ticket} title="Open Tickets" value={stats.openTickets} subtitle="Needs attention" color="amber" />
        <StatCard icon={FileText} title="Documents" value={stats.documents} subtitle="Uploaded" />
        <StatCard icon={Building2} title="Bookings" value={cpStats.bookingCount} subtitle="From Top 10 workbook" color="emerald" />
      </div>
      <Suspense fallback={<div className="py-4 text-center text-stone-400 text-sm">Loading commission structure…</div>}>
        <CommissionStructureCard userRole="cp" myLevel={null} />
      </Suspense>
    </div>
  );

  return (
    <>
      <Suspense fallback={<div className="py-10 text-center text-stone-400">Loading workbook…</div>}>
        <MySheetsTab user={user} overviewContent={overviewContent} />
      </Suspense>
      {cpLeads.length > 0 && (
        <Suspense fallback={null}>
          <CallActionWidget
            leads={cpLeads}
            leadMeta={cpLeadMeta}
            userId={user?.employeeId || user?.id || 'cp'}
            userName={user?.name || ''}
          />
        </Suspense>
      )}
    </>
  );
};

// Customer Dashboard (Basic)
const CustomerDashboardBasic = ({ user, stats, recentTickets }) => (
  <>
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
      <StatCard icon={Building2} title="My Properties" value="0" subtitle="Booked/Interested" />
      <StatCard icon={Ticket} title="Support Tickets" value={stats.totalTickets} subtitle="Total raised" />
      <StatCard icon={FileText} title="Documents" value={stats.documents} subtitle="Uploaded" />
      <StatCard icon={CheckCircle} title="Status" value="Active" subtitle="Account status" color="emerald" />
    </div>

    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card>
        <CardHeader>
          <CardTitle>Property Interest</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-stone-500 text-center py-8">
            Browse and select properties from the "My Properties" section
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Document Checklist</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {['7/12 Extract', 'Agriculture Certificate', 'Aadhaar Card', 'PAN Card', 'Photo'].map((doc) => (
              <div key={doc} className="flex items-center justify-between p-2 bg-stone-50 rounded">
                <span className="text-sm">{doc}</span>
                <span className="text-xs text-amber-600">Pending</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  </>
);

// Employee Dashboard
const EmployeeDashboard = ({ user, stats, recentTickets }) => (
  <>
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
      <StatCard icon={Ticket} title="My Tickets" value={stats.totalTickets} subtitle="Total raised" />
      <StatCard icon={AlertCircle} title="Open Tickets" value={stats.openTickets} subtitle="Pending" color="amber" />
      <StatCard icon={FileText} title="Documents" value={stats.documents} subtitle="Uploaded" />
    </div>

    <Card>
      <CardHeader>
        <CardTitle>Recent Tickets</CardTitle>
      </CardHeader>
      <CardContent>
        {recentTickets.length === 0 ? (
          <p className="text-stone-500 text-center py-8">No tickets raised yet</p>
        ) : (
          <div className="space-y-3">
            {recentTickets.map((ticket) => (
              <div key={ticket.ticketId} className="flex items-center justify-between p-3 bg-stone-50 rounded-lg">
                <div>
                  <p className="font-medium text-stone-900">{ticket.subject}</p>
                  <p className="text-xs text-stone-500">{ticket.ticketId}</p>
                </div>
                <span className={`px-2 py-1 text-xs rounded-full ${
                  ticket.status === 'open' ? 'bg-amber-100 text-amber-700' :
                  ticket.status === 'resolved' ? 'bg-green-100 text-green-700' :
                  'bg-stone-100 text-stone-700'
                }`}>
                  {ticket.status}
                </span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  </>
);

// Super Admin Dashboard - Shows all department updates
const SuperAdminDashboard = ({ user, stats, recentTickets }) => {
  const [departmentStats, setDepartmentStats] = useState([]);
  
  useEffect(() => {
    // Mock department stats
    setDepartmentStats([
      { name: 'HR', icon: Users, pending: 3, total: 25, color: 'bg-blue-500' },
      { name: 'Marketing', icon: TrendingUp, pending: 5, total: 42, color: 'bg-purple-500' },
      { name: 'Finance', icon: Building2, pending: 2, total: 18, color: 'bg-emerald-500' },
      { name: 'Operations', icon: Building2, pending: 1, total: 12, color: 'bg-amber-500' },
      { name: 'DM Channel', icon: Users, pending: 8, total: 65, color: 'bg-teal-500' },
      { name: 'IT', icon: Building2, pending: 4, total: 15, color: 'bg-cyan-500' },
    ]);
  }, []);

  return (
    <>
      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard icon={Ticket} title="Total Tickets" value={stats.totalTickets} subtitle="All departments" />
        <StatCard icon={AlertCircle} title="Open Tickets" value={stats.openTickets} subtitle="Needs attention" color="amber" />
        <StatCard icon={FileText} title="Documents" value={stats.documents} subtitle="All uploads" />
        <StatCard icon={Users} title="Pending Approvals" value={stats.pendingApprovals || 0} subtitle="User registrations" color={stats.pendingApprovals > 0 ? "red" : "stone"} />
      </div>

      {/* Department Overview */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-emerald-600" />
            Department Overview
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {departmentStats.map(dept => (
                <div key={dept.name} className="p-4 border rounded-lg text-center hover:shadow-md transition-shadow">
                  <div className={`w-10 h-10 ${dept.color} rounded-lg flex items-center justify-center mx-auto mb-2`}>
                    {React.createElement(dept.icon, {className: 'w-5 h-5 text-white'})}
                  </div>
                  <p className="font-medium text-stone-800">{dept.name}</p>
                  <p className="text-2xl font-bold text-stone-900">{dept.total}</p>
                  {dept.pending > 0 && (
                    <p className="text-xs text-amber-600">{dept.pending} pending</p>
                  )}
                </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Ticket className="w-5 h-5 text-emerald-600" />
              Recent Tickets (All Depts)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {recentTickets.length === 0 ? (
              <p className="text-stone-500 text-center py-4">No tickets yet</p>
            ) : (
              <div className="space-y-3">
                {recentTickets.map((ticket) => (
                  <div key={ticket.ticketId} className="flex items-center justify-between p-3 bg-stone-50 rounded-lg">
                    <div>
                      <p className="font-medium text-stone-900">{ticket.subject}</p>
                      <p className="text-xs text-stone-500">{ticket.ticketId} • {ticket.raisedByName}</p>
                    </div>
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      ticket.status === 'open' ? 'bg-amber-100 text-amber-700' :
                      ticket.status === 'resolved' ? 'bg-green-100 text-green-700' :
                      'bg-stone-100 text-stone-700'
                    }`}>
                      {ticket.status}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-amber-600" />
              Quick Actions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <a href="/dashboard/users" className="block p-3 bg-emerald-50 rounded-lg hover:bg-emerald-100 transition-colors">
                <p className="font-medium text-emerald-900">Manage Users</p>
                <p className="text-xs text-emerald-700">Approve pending registrations</p>
              </a>
              <a href="/dashboard/tickets" className="block p-3 bg-amber-50 rounded-lg hover:bg-amber-100 transition-colors">
                <p className="font-medium text-amber-900">View All Tickets</p>
                <p className="text-xs text-amber-700">Manage all department tickets</p>
              </a>
              <a href="/dashboard/excel" className="block p-3 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors">
                <p className="font-medium text-blue-900">Excel Reports</p>
                <p className="text-xs text-blue-700">View department reports</p>
              </a>
              <a href="/dashboard/contests" className="block p-3 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors">
                <p className="font-medium text-purple-900">Contests</p>
                <p className="text-xs text-purple-700">View contest achievements</p>
              </a>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
};

export default DashboardHome;
